import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "../../../../lib/http/axiosConfig";
import { setDiagnosisFocus } from "../../../../features/TabSlice";
import { useLang, useT } from "../../../../i18n";
import { formatReadableName } from "../../../../charts/data/naming";
import "./DiagnosisRuns.css";

// 조회 탭 병해충 영역. 촬영 회차를 하나씩 세로로 쌓는다.
//
// 회차마다 드론이 도는 구역이 달라서, 여러 회차를 기간으로 뭉치면 숫자가 언제 것인지
// 알 수 없게 된다. 선 그래프로 이으면 "다른 데를 찍은 것"이 "병이 나았다"로 보이기까지 한다.
// 그래서 평균도 선도 쓰지 않고, 회차별 기록을 그대로 나열한다.
//
// 촬영 칸 수를 늘 옆에 붙여 두는 게 핵심이다. 그래야 두 회차를 비교해도 되는지
// 보는 사람이 스스로 판단할 수 있다.

// dot 은 칸 색과 같은 표시, value 는 숫자 색.
// 안 찍음은 "모른다"는 뜻이라 값은 흰색으로 남겨 눈에 걸리게 한다 —
// 회색으로 깔면 괜찮은 것처럼 읽힌다.
// labelKey 는 t() 로 풀어 쓴다(언어를 바꾸면 따라간다).
const GRADES = [
  { key: "highCells", labelKey: "data.grade.high", dot: "high", value: "high" },
  { key: "midCells", labelKey: "data.grade.mid", dot: "mid", value: "mid" },
  { key: "okCells", labelKey: "data.grade.ok", dot: "ok", value: "ok" },
  // "이번에"는 빼도 된다. 회차 카드 안이라 어느 회차인지는 제목이 말해주고,
  // 맨 아래 "이 기간에 한 번도 안 찍은 칸"과는 자리로 구분된다.
  { key: "unscannedCells", labelKey: "data.grade.unscanned", dot: "none", value: "unseen" },
];

const formatDate = (iso) => {
  if (typeof iso !== "string") return "";
  const parts = iso.split("-");
  if (parts.length < 3) return iso;
  return `${Number(parts[1])}/${Number(parts[2])}`;
};

// reloadToken 은 툴바 새로고침 nonce. 같은 기간으로 다시 받게 한다.
// onLoadingChange / onLoaded 는 툴바의 새로고침 버튼 잠금과 "HH:MM 기준" 표시용.
export default function DiagnosisRuns({
  farmId,
  from,
  to,
  reloadToken = 0,
  onLoadingChange,
  onLoaded,
  // 데모 계정용. 값이 있으면 조회를 건너뛰고 이 응답을 그대로 그린다.
  // 모양은 서버 응답(data)과 같아야 한다 — demo/demoDashboard.js 가 만든다.
  demoData = null,
}) {
  const t = useT();
  const [lang] = useLang();
  // 병명은 서버 값이다. 영어 모드면 nameEn(CABBAGE_SOFT_ROT 꼴)을 풀어 쓰고,
  // 없으면(옛 응답) 한글로 둔다. 추세 차트와 같은 규칙.
  const diseaseName = (d) =>
    lang === "en"
      ? formatReadableName(d.nameEn) || d.nameKo
      : d.nameKo || formatReadableName(d.nameEn);
  const dispatch = useDispatch();
  // 지금 지도에 떠 있는 회차. 지도 쪽 드롭다운으로 바꿔도 여기 표시가 따라 움직인다.
  const focus = useSelector((state) => state.tab.diagnosisFocus);
  const activeTab = useSelector((state) => state.tab.activeTab);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // 문구가 아니라 실패 여부만 둔다. 문구는 그릴 때 t() 로 만들어 언어 전환을 따라가게 한다.
  const [error, setError] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!farmId) {
      setData(null);
      setIsLoading(false);
      onLoadingChange?.(false);
      return undefined;
    }

    // 데모는 BE 를 타지 않는다. 조회 상태만 실계정과 같게 맞춰 두고 바로 그린다.
    if (demoData) {
      setData(demoData);
      setOpenId(demoData.runs?.[0]?.missionLogId ?? null);
      setError(false);
      setIsLoading(false);
      onLoadingChange?.(false);
      onLoaded?.(new Date());
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setIsLoading(true);
      onLoadingChange?.(true);
      setError(false);
      try {
        const res = await axios.get(`/api/dashboard/${farmId}/diagnosis/runs`, {
          params: { from, to },
        });
        if (cancelled) return;
        const payload = res?.data?.data ?? null;
        setData(payload);
        setOpenId(payload?.runs?.[0]?.missionLogId ?? null);
        onLoaded?.(new Date());
      } catch (e) {
        if (cancelled) return;
        setData(null);
        setError(true);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          onLoadingChange?.(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // reloadToken 은 값 자체를 안 쓴다 — 바뀌면 다시 받으라는 신호로만 둔다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId, from, to, reloadToken, onLoadingChange, onLoaded, demoData]);

  // 농장이나 기간이 바뀌면 목록이 통째로 갈린다. 지도에 띄워 둔 회차가
  // 새 목록에 없을 수 있으므로 먼저 놓는다(지도도 같이 평소 화면으로 돌아온다).
  useEffect(() => {
    dispatch(setDiagnosisFocus(null));
  }, [dispatch, farmId, from, to]);

  // 다른 탭으로 옮기면 놓는다. 미션 탭은 셀을 고르는 화면인데 진단 오버레이가
  // 켜져 있으면 고른 셀도 드론도 안 보인다.
  // 탭이 비어 있는 경우(모바일에서 패널을 접은 상태)는 건드리지 않는다 —
  // 지도를 보려고 접은 것이라 여기서 놓으면 방금 고른 회차가 사라진다.
  useEffect(() => {
    if (activeTab && activeTab !== "Data") dispatch(setDiagnosisFocus(null));
  }, [activeTab, dispatch]);

  useEffect(() => () => {
    dispatch(setDiagnosisFocus(null));
  }, [dispatch]);

  const runs = useMemo(() => data?.runs ?? [], [data]);

  if (isLoading) {
    return <div className="dr-state">{t("data.runs.loading")}</div>;
  }
  if (error) {
    return <div className="dr-state dr-state--error">{t("data.runs.loadError")}</div>;
  }
  if (!runs.length) {
    // 세 경우를 구분한다. 뭉뚱그리면 "괜찮은 밭"처럼 보인다.
    //   완료됐는데 결과 없음 → 분석이 안 붙은 것. 문제다.
    //   중단된 미션만 있음   → 애초에 촬영이 안 된 것.
    //   아무것도 없음        → 안 날린 것.
    const empty = data?.emptyRuns ?? 0;
    const aborted = data?.abortedRuns ?? 0;
    return (
      <div className="dr-state">
        {empty > 0 ? (
          <>
            {t("data.runs.empty.noResults", { count: empty })}
            <br />
            {t("data.runs.empty.noResultsHint")}
          </>
        ) : aborted > 0 ? (
          <>
            {t("data.runs.empty.noCompleted")}
            <br />
            {t("data.runs.empty.abortedCount", { count: aborted })}
          </>
        ) : (
          <>
            {t("data.runs.empty.noRecords")}
            <br />
            {t("data.runs.empty.noRecordsHint")}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="dr-wrap">
      <div className="dr-head">
        <span>{t("data.runs.count", { count: runs.length })}</span>
        <span className="dr-total">{t("data.runs.totalCells", { count: data.totalCells })}</span>
      </div>

      {runs.map((run) => {
        const isOpen = run.missionLogId === openId;
        // 지도에 이 회차가 떠 있는지. 펼침(isOpen)과 따로 둔다 —
        // 목록이 처음 뜰 때 첫 회차가 저절로 펼쳐지는데, 그것만으로 지도가
        // 진단 모드로 바뀌면 사용자가 누르지도 않은 화면 전환이 된다.
        const isOnMap = focus?.missionLogId === run.missionLogId;
        return (
          <div
            key={run.missionLogId}
            className={`dr-run${isOpen ? " is-open" : ""}${
              isOnMap ? " is-onmap" : ""
            }`}
          >
            <button
              type="button"
              className="dr-run-head"
              aria-expanded={isOpen}
              aria-pressed={isOnMap}
              title={isOnMap ? t("data.runs.removeFromMap") : t("data.runs.showOnMap")}
              onClick={() => {
                // 누르는 뜻은 "이 회차를 지도에서 보기"다. 펼침은 거기 따라간다.
                // 이미 보고 있는 회차를 다시 누르면 해제 — 접고 지도도 되돌린다.
                if (isOnMap) {
                  setOpenId(null);
                  dispatch(setDiagnosisFocus(null));
                  return;
                }
                setOpenId(run.missionLogId);
                dispatch(
                  setDiagnosisFocus({
                    missionLogId: run.missionLogId,
                    date: run.date,
                    time: run.time,
                    at: Date.now(),
                  })
                );
              }}
            >
              <span className="dr-caret">{isOpen ? "▾" : "▸"}</span>
              <span className="dr-when">
                {formatDate(run.date)} {run.time}
                {/* 고도가 다르면 사진 한 장이 덮는 면적이 달라 "촬영 N칸"의 무게가 회차마다 다르다.
                    펼치지 않아도 회차끼리 비교되도록 머리글에 둔다. */}
                {run.altitude != null
                  ? ` · ${t("data.runs.altitude", { value: run.altitude })}`
                  : ""}
              </span>
              {isOnMap ? <span className="dr-onmap">{t("data.runs.onMapBadge")}</span> : null}
              <span className="dr-count">{t("data.runs.scanned", { count: run.scannedCells })}</span>
            </button>

            {isOpen ? (
              <div className="dr-body">
                {GRADES.map((g) => (
                  <div className="dr-row" key={g.key}>
                    <span className="dr-k">
                      <i className={`dr-dot dr-dot--${g.dot}`} />
                      {t(g.labelKey)}
                    </span>
                    {/* 0 은 회색으로 죽인다. 기본 흰색으로 두면 "심각 0칸"이
                        "안 찍음 60칸"과 같은 무게로 읽힌다. */}
                    <span
                      className={`dr-v dr-v--${run[g.key] ? g.value : "none"}`}
                    >
                      {t("data.runs.cells", { count: run[g.key] })}
                    </span>
                  </div>
                ))}

                <table className="dr-table">
                  <caption>{t("data.runs.table.caption")}</caption>
                  <thead>
                    <tr>
                      <th>{t("data.runs.table.disease")}</th>
                      <th>{t("data.grade.high")}</th>
                      <th>{t("data.grade.mid")}</th>
                      <th>{t("data.runs.table.images")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {run.diseases.length ? (
                      run.diseases.map((d) => (
                        <tr key={d.pestId}>
                          <td>{diseaseName(d)}</td>
                          <td className={d.highCells ? "dr-v--high" : "dr-v--none"}>
                            {d.highCells}
                          </td>
                          <td className={d.midCells ? "dr-v--mid" : "dr-v--none"}>
                            {d.midCells}
                          </td>
                          <td>{d.cropCount}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="dr-empty">
                          {t("data.runs.table.none")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dr-mini">
                {t("data.grade.high")} {run.highCells} · {t("data.grade.mid")} {run.midCells} ·{" "}
                {t("data.grade.ok")} {run.okCells}
              </div>
            )}
          </div>
        );
      })}

      <div className="dr-never">
        <span>{t("data.runs.neverScanned")}</span>
        <b>{t("data.runs.cells", { count: data.neverScannedCells })}</b>
      </div>
      {/* <div className="dr-never-note">
        고른 기간의 어느 회차에도 포함된 적 없는 칸입니다. 다음 비행에 넣을 후보입니다.
      </div> */}

      {(data.emptyRuns > 0 || data.abortedRuns > 0) && (
        <div className="dr-never-note">
          {t("data.runs.note.excluded", {
            items: [
              data.emptyRuns > 0
                ? t("data.runs.note.emptyRuns", { count: data.emptyRuns })
                : "",
              data.abortedRuns > 0
                ? t("data.runs.note.aborted", { count: data.abortedRuns })
                : "",
            ]
              .filter(Boolean)
              .join(" · "),
          })}
        </div>
      )}
    </div>
  );
}
